import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@gokrew/ui';
import { TeamsLogo } from '@gokrew/ui/components/TeamsLogo';
import { TranslationNS } from '@gokrew/models/constants';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/constants/query-keys';
import styles from './TeamsConnectPage.module.css';

/** Where the backend's OIDC callback redirects. It only ever reports — the bind
 * already happened server-side. */
export default function TeamsCallbackPage() {
  const { t } = useTranslation(TranslationNS.teams);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const error = params.get('error');
  const tenantId = params.get('tenantId');
  const companyId = params.get('companyId');
  const connected = params.get('status') === 'connected';

  useEffect(() => {
    if (connected) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.TeamsStatus, companyId],
      });
    }
  }, [connected, companyId, queryClient]);

  const goBack = () => navigate('/integrations');

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <TeamsLogo size={45} />

        {!error && !connected && (
          <>
            <Loader2 size={40} className={styles.spinner} />
            <p className={styles.title}>{t('teamsCallback.loading.title')}</p>
          </>
        )}

        {connected && (
          <>
            <div className={styles.iconOk}>
              <CheckCircle2 size={32} />
            </div>
            <div>
              <p className={styles.title}>{t('teamsCallback.success.title')}</p>
              {/* The bind is only provisional until the bot hears from this
                  tenant, so the domain is shown for the admin to sanity-check. */}
              <p className={styles.description}>
                {t('teamsCallback.success.description')}
              </p>
              {tenantId && <p className={styles.tenant}>{tenantId}</p>}
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={goBack}
              className={styles.primaryBtn}
            >
              {t('teamsCallback.success.back')}
            </Button>
          </>
        )}

        {error && (
          <>
            <div className={styles.iconBad}>
              <XCircle size={32} />
            </div>
            <div>
              <p className={styles.title}>{t('teamsCallback.error.title')}</p>
              <p className={styles.description}>
                {t(`teamsCallback.error.${error}`, {
                  defaultValue: t('teamsCallback.error.fallback'),
                })}
              </p>
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={goBack}
              className={styles.primaryBtn}
            >
              {t('teamsCallback.error.back')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
